import React, { Fragment, useState, useEffect } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import AppPagination from "../Common/AppPagination";
import PageTitle from "../../layouts/PageTitle";
import { Row, Col, Card, Table, Dropdown, Modal, Button, Form } from "react-bootstrap";
import api, { getFileUrl } from "../../../api";
import swal from "sweetalert";

const VendorCategories = () => {
 // Categories State
 const [categories, setCategories] = useState([]);
 const [loading, setLoading] = useState(false);
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0
 });

 // Filter State
 const [filters, setFilters] = usePersistentFilters("vendor_categories_filters", {
 search: "",
 page: 1,
 limit: 10
 });

 // Modal States
 const [postModal, setPostModal] = useState(false);
 const [editModal, setEditModal] = useState(false);

 // Add Form State
 const [newCategory, setNewCategory] = useState({
 name: "",
 description: "",
 image: ""
 });
 const [uploadingNewImage, setUploadingNewImage] = useState(false);

 // Edit Form State
 const [editCategory, setEditCategory] = useState(null);
 const [uploadingEditImage, setUploadingEditImage] = useState(false);

 useEffect(() => {
 fetchCategories();
 }, [filters.page, filters.limit]);

 const fetchCategories = async () => {
 try {
 setLoading(true);
 const queryParams = new URLSearchParams();
 if (filters.search) queryParams.append('search', filters.search);
 queryParams.append('page', filters.page);
 queryParams.append('limit', filters.limit);

 const response = await api.get(`/vendor-categories?${queryParams.toString()}`);
 if (response.data.success) {
 setCategories(response.data.data);
 setPagination(response.data.pagination);
 }
 } catch (error) {
 console.error("Error fetching categories:", error);
 swal("Error", "Failed to fetch categories", "error");
 } finally {
 setLoading(false);
 }
 };

 // File Upload Handler for Add
 const handleNewImageUpload = async (e) => {
 const file = e.target.files[0];
 if (!file) return;

 try {
 setUploadingNewImage(true);
 const formData = new FormData();
 formData.append("image", file);

 const response = await api.post("/vendor-categories/upload/single", formData, {
 headers: { "Content-Type": "multipart/form-data" }
 });

 if (response.data.success) {
 setNewCategory((prev) => ({
 ...prev,
 image: response.data.fileUrl
 }));
 swal("Uploaded!", `${file.name} uploaded successfully`, "success");
 }
 } catch (error) {
 console.error("Error uploading image:", error);
 swal("Error", error.response?.data?.message || "Failed to upload image", "error");
 } finally {
 setUploadingNewImage(false);
 }
 };

 // File Upload Handler for Edit
 const handleEditImageUpload = async (e) => {
 const file = e.target.files[0];
 if (!file) return;

 try {
 setUploadingEditImage(true);
 const formData = new FormData();
 formData.append("image", file);

 const response = await api.post("/vendor-categories/upload/single", formData, {
 headers: { "Content-Type": "multipart/form-data" }
 });

 if (response.data.success) {
 setEditCategory((prev) => ({
 ...prev,
 image: response.data.fileUrl
 }));
 swal("Uploaded!", `${file.name} uploaded successfully`, "success");
 }
 } catch (error) {
 console.error("Error uploading image:", error);
 swal("Error", error.response?.data?.message || "Failed to upload image", "error");
 } finally {
 setUploadingEditImage(false);
 }
 };

 // Input Handlers
 const handleAddFormChange = (e) => {
 const { name, value } = e.target;
 setNewCategory((prev) => ({ ...prev, [name]: value }));
 };

 const handleEditFormChange = (e) => {
 const { name, value } = e.target;
 setEditCategory((prev) => ({ ...prev, [name]: value }));
 };

 // Submit Add
 const handleAddFormSubmit = async (e) => {
 e.preventDefault();

 if (!newCategory.name)
 return swal("Warning", "Please enter category name", "warning");
 if (!newCategory.description)
 return swal("Warning", "Please enter description", "warning");

 try {
 setLoading(true);
 const response = await api.post("/vendor-categories", newCategory);

 if (response.data.success) {
 swal("Success", "Category created successfully!", "success");
 setNewCategory({ name: "", description: "", image: "" });
 setPostModal(false);
 fetchCategories();
 }
 } catch (error) {
 console.error("Error creating category:", error);
 swal("Error", error.response?.data?.message || "Failed to create category", "error");
 } finally {
 setLoading(false);
 }
 };

 // Submit Edit
 const handleEditFormSubmit = async (e) => {
 e.preventDefault();

 if (!editCategory.name)
 return swal("Warning", "Please enter category name", "warning");
 if (!editCategory.description)
 return swal("Warning", "Please enter description", "warning");

 try {
 setLoading(true);
 const response = await api.patch(`/vendor-categories/${editCategory._id}`, {
 name: editCategory.name,
 description: editCategory.description,
 image: editCategory.image
 });

 if (response.data.success) {
 swal("Success", "Category updated successfully!", "success");
 setEditCategory(null);
 setEditModal(false);
 fetchCategories();
 }
 } catch (error) {
 console.error("Error updating category:", error);
 swal("Error", error.response?.data?.message || "Failed to update category", "error");
 } finally {
 setLoading(false);
 }
 };

 // Delete Category
 const handleDelete = async (id) => {
 const confirm = await swal({
 title: "Delete?",
 text: "This category will be permanently deleted.",
 icon: "warning",
 buttons: true,
 dangerMode: true
 });

 if (!confirm) return;

 try {
 const response = await api.delete(`/vendor-categories/${id}`);
 if (response.data.success) {
 swal("Deleted!", "Category deleted successfully", "success");
 fetchCategories();
 }
 } catch (error) {
 console.error("Error deleting category:", error);
 swal("Error", "Failed to delete category", "error");
 }
 };

 const handleSearch = () => {
 setFilters((prev) => ({ ...prev, page: 1 }));
 fetchCategories();
 };

 const handlePageChange = (newPage) => {
 setFilters((prev) => ({ ...prev, page: newPage }));
 };

 // SVG Menu
 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
 <circle fill="currentColor" cx="5" cy="12" r="2"></circle>
 <circle fill="currentColor" cx="12" cy="12" r="2"></circle>
 <circle fill="currentColor" cx="19" cy="12" r="2"></circle>
 </g>
 </svg>
 );

 return (
 <Fragment>
 <PageTitle activeMenu="Vendor Categories" motherMenu="Vendors" />

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between">
 <Card.Title>Vendor Category List</Card.Title>
 <Button variant="primary" onClick={() => setPostModal(true)}>
 + Add Category
 </Button>
 </Card.Header>

 <Card.Body>
 {loading ? (
 <div className="text-center py-5">
 <div className="spinner-border text-primary"></div>
 <p className="mt-3">Loading categories...</p>
 </div>
 ) : categories.length === 0 ? (
 <div className="text-center py-5">
 <p className="text-muted">No categories found. Create one to get started!</p>
 <Button variant="primary" onClick={() => setPostModal(true)}>
 + Add Category
 </Button>
 </div>
 ) : (
 <>
 <div className="table-responsive">
 <Table hover className="align-middle mb-0">
  <thead className="bg-light">
  <tr>
  <th className="px-4 py-3 text-uppercase fs-12 font-w600 text-muted text-start" style={{ width: "60px" }}>#</th>
  <th className="py-3 text-uppercase fs-12 font-w600 text-muted text-start" style={{ width: "90px" }}>Image</th>
  <th className="py-3 text-uppercase fs-12 font-w600 text-muted text-start" style={{ width: "260px" }}>Name</th>
  <th className="py-3 text-uppercase fs-12 font-w600 text-muted text-start">Description</th>
  <th className="text-end px-4 py-3 text-uppercase fs-12 font-w600 text-muted" style={{ width: "100px" }}>Actions</th>
  </tr>
  </thead>

  <tbody>
  {categories.map((cat, i) => (
  <tr key={cat._id}>
  <td className="px-4 text-muted font-w600 text-start">{(pagination.currentPage - 1) * filters.limit + i + 1}</td>
  <td className="text-start">
  <div 
  className="cat-img-box rounded shadow-sm bg-light d-flex align-items-center justify-content-center text-muted"
  style={{ width: "45px", height: "45px", overflow: "hidden", flexShrink: 0 }}
  >
  {cat.image ? (
  <img
  src={getFileUrl(cat.image)}
  alt=""
  style={{ width: "100%", height: "100%", objectFit: "cover" }}
  onError={(e) => {
    e.currentTarget.style.display = "none";
    if (e.currentTarget.parentElement) {
      e.currentTarget.parentElement.innerHTML = '<i class="fas fa-boxes text-muted" style="font-size: 16px;"></i>';
    }
  }}
  />
  ) : (
  <i className="fas fa-boxes text-muted" style={{ fontSize: "16px" }}></i>
  )}
  </div>
  </td>
  <td className="text-start" style={{ width: "260px" }}>
  <strong className="text-dark fs-14">{cat.name}</strong>
  </td>
  <td className="text-muted small text-start">
  {cat.description || "—"}
  </td>

  <td className="text-end px-4">
  <Dropdown className="ms-auto">
  <Dropdown.Toggle variant="" as="div" className="btn-link i-false no-caret" style={{ cursor: "pointer" }}>
  {svgMenu}
  </Dropdown.Toggle>

  <Dropdown.Menu align="end" className="border-0 shadow-sm" as={PortalMenu}>
  <Dropdown.Item
  className="py-2"
  onClick={() => {
  setEditCategory(cat);
  setEditModal(true);
  }}
  >
  <i className="fas fa-edit me-2 text-primary"></i>Edit
  </Dropdown.Item>

  <Dropdown.Item
  className="text-danger py-2"
  onClick={() => handleDelete(cat._id)}
  >
  <i className="fas fa-trash me-2"></i>Delete
  </Dropdown.Item>
  </Dropdown.Menu>
  </Dropdown>
  </td>
  </tr>
  ))}
  </tbody>
  </Table>
 </div>

 {/* Pagination */}
 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={categories.length}
 limit={filters.limit}
 onPageChange={handlePageChange}
 />
 </>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* Add Category Modal */}
 <Modal show={postModal} onHide={() => setPostModal(false)} centered>
 <form onSubmit={handleAddFormSubmit}>
 <Modal.Header closeButton>
 <Modal.Title>Add Vendor Category</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 <Form.Group className="mb-3">
 <Form.Label>
 Category Name <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 type="text"
 name="name"
 value={newCategory.name}
 onChange={handleAddFormChange}
 placeholder="Enter category name"
 required
 />
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>
 Description <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 as="textarea"
 name="description"
 value={newCategory.description}
 onChange={handleAddFormChange}
 placeholder="Enter description"
 rows={3}
 required
 />
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>
 Upload Image
 </Form.Label>

 <Form.Control
 type="file"
 onChange={handleNewImageUpload}
 accept="image/*"
 disabled={uploadingNewImage}
 />

 {uploadingNewImage && (
 <div className="mt-2">
 <span className="spinner-border spinner-border-sm me-2"></span>
 Uploading...
 </div>
 )}

 {newCategory.image && (
 <div className="mt-2">
 <img
 src={getFileUrl(newCategory.image)}
 alt="Preview"
 width="100"
 height="100"
 className="rounded"
 style={{ objectFit: "cover" }}
 />
 </div>
 )}
 </Form.Group>
 </Modal.Body>

 <Modal.Footer>
 <Button
 type="submit"
 variant="primary"
 disabled={loading || uploadingNewImage}
 >
 {loading ? (
 <>
 <span className="spinner-border spinner-border-sm me-2"></span>
 Adding...
 </>
 ) : (
 <>
 <i className="fas fa-save me-2"></i>Add
 </>
 )}
 </Button>

 <Button variant="danger" onClick={() => setPostModal(false)} disabled={loading}>
 <i className="fas fa-times me-2"></i>Cancel
 </Button>
 </Modal.Footer>
 </form>
 </Modal>

 {/* Edit Category Modal */}
 <Modal show={editModal} onHide={() => setEditModal(false)} centered>
 <form onSubmit={handleEditFormSubmit}>
 <Modal.Header closeButton>
 <Modal.Title>Edit Vendor Category</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {editCategory && (
 <>
 <Form.Group className="mb-3">
 <Form.Label>
 Category Name <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 type="text"
 name="name"
 value={editCategory.name}
 onChange={handleEditFormChange}
 placeholder="Enter category name"
 required
 />
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>
 Description <span className="text-danger">*</span>
 </Form.Label>
 <Form.Control
 as="textarea"
 name="description"
 value={editCategory.description}
 onChange={handleEditFormChange}
 placeholder="Enter description"
 rows={3}
 required
 />
 </Form.Group>

 <Form.Group className="mb-3">
 <Form.Label>Upload Image</Form.Label>

 <Form.Control
 type="file"
 onChange={handleEditImageUpload}
 accept="image/*"
 disabled={uploadingEditImage}
 />

 {uploadingEditImage && (
 <div className="mt-2">
 <span className="spinner-border spinner-border-sm me-2"></span>
 Uploading...
 </div>
 )}

 {editCategory.image && (
 <div className="mt-2">
 <img
 src={getFileUrl(editCategory.image)}
 alt="Preview"
 width="100"
 height="100"
 className="rounded"
 style={{ objectFit: "cover" }}
 />
 </div>
 )}
 </Form.Group>
 </>
 )}
 </Modal.Body>

 <Modal.Footer>
 <Button type="submit" variant="primary" disabled={loading || uploadingEditImage}>
 {loading ? (
 <>
 <span className="spinner-border spinner-border-sm me-2"></span>
 Saving...
 </>
 ) : (
 <>
 <i className="fas fa-save me-2"></i>Save
 </>
 )}
 </Button>

 <Button variant="danger" onClick={() => setEditModal(false)} disabled={loading}>
 <i className="fas fa-times me-2"></i>Cancel
 </Button>
 </Modal.Footer>
 </form>
 </Modal>
 
  <style>{`
  .fs-12 { font-size: 12px; }
  .fs-14 { font-size: 14px; }
  .font-w600 { font-weight: 600; }
  .table th, .table td {
    text-align: left !important;
  }
  .table th.text-end, .table td.text-end {
    text-align: right !important;
  }
  .cat-img-box {
    border: 1px solid #f1f2f6;
  }

  /* Dark mode support */
  [data-theme-version="dark"] .cat-img-box {
    background-color: #161717 !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
  }
  [data-theme-version="dark"] thead.bg-light th {
    background-color: #161717 !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
    color: #94a3b8 !important;
  }
  [data-theme-version="dark"] .table tbody td {
    border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
  }
  [data-theme-version="dark"] .table-hover tbody tr:hover {
    background-color: rgba(255, 255, 255, 0.03) !important;
  }
  [data-theme-version="dark"] .text-dark {
    color: #f1f5f9 !important;
  }
  [data-theme-version="dark"] .modal-content {
    background-color: #1e1e2d !important;
    color: #f1f5f9 !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  [data-theme-version="dark"] .modal-header {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  [data-theme-version="dark"] .modal-header .btn-close {
    filter: invert(1) grayscale(100%) brightness(200%) !important;
  }
  [data-theme-version="dark"] .modal-title {
    color: #ffffff !important;
  }
  [data-theme-version="dark"] .modal-body .form-control {
    background-color: #161717 !important;
    border: 1px solid #2b2b2b !important;
    color: #ffffff !important;
  }
  [data-theme-version="dark"] .modal-body .form-control::placeholder {
    color: #64748b !important;
  }
  [data-theme-version="dark"] .modal-body .form-label {
    color: #e2e8f0 !important;
  }
  [data-theme-version="dark"] .modal-footer {
    border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
  }
  `}</style>
  </Fragment>
 );
};

export default VendorCategories;
